import { useLongPress } from "@react-aria/interactions";
import type { DOMAttributes, FocusableElement } from "@react-types/shared";

export interface UseLongPressGestureOptions {
  /** Accessible description of what a long press does, announced by screen readers. */
  accessibilityDescription?: string;
  isDisabled?: boolean;
  onLongPress: () => void;
  /** Milliseconds to hold. React Aria's default is 500. */
  threshold?: number;
}

/**
 * Press-and-hold gesture, for touch affordances that have no room for a visible control.
 *
 * Always pair it with something reachable another way — a hidden gesture is not an affordance on its
 * own, and neither a keyboard nor a screen reader can perform one.
 *
 * While armed, the returned props include `style` disabling the iOS callout and text selection:
 * without it Safari raises its own copy/lookup menu on top of whatever the long press opened, and
 * toggling `user-select` at press start is too late — WebKit fixes selectability when the touch
 * begins. That makes suppression the price of arming, so pass `isDisabled` wherever selecting the
 * text matters more than the hold (e.g. on hover-capable devices with a visible control instead).
 */
export function useLongPressGesture({
  accessibilityDescription,
  isDisabled,
  onLongPress,
  threshold,
}: UseLongPressGestureOptions): DOMAttributes<FocusableElement> {
  const { longPressProps } = useLongPress({
    accessibilityDescription,
    isDisabled,
    onLongPress,
    threshold,
  });

  if (isDisabled) return longPressProps;

  return {
    ...longPressProps,
    style: { WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" },
  };
}
